import { buildQueryString } from '@msaf/core-common'
import { SearchQuery } from '../types/state'
import { RequestOptions } from '@msaf/generic-search-common'

async function _makeRequest(input: RequestInfo, init?: RequestInit, options?: RequestOptions) {
  let reqInit = init ?? {}

  // Set auth for request
  if (options?.authStrategy === 'token') {
    if (!options.authToken) {
      throw new Error('No auth token set for request to generic-search backend')
    }

    if (Array.isArray(reqInit.headers)) {
      throw new Error('Array type is not currently supported for generic search header init')
    }

    const bearer: string = `Bearer ${options.authToken}`

    // Handle different ways `headers` object can exist in RequestInit
    if (!reqInit.headers) {
      reqInit.headers = {
        Authorization: bearer,
      }
    } else if ('Authorization' in reqInit.headers) {
      reqInit.headers['Authorization'] = bearer
    } else if (reqInit.headers instanceof Headers) {
      reqInit.headers.set('Authorization', bearer)
    } else {
      reqInit.headers['Authorization'] = bearer
    }
  }

  // Set URL for request
  if (typeof input !== 'string') {
    throw new Error('Request input that is not a string literal is not currently supported for generic search')
  }

  // De-dup slashes in request url
  const reqInput = `${options?.urlPrefix ?? '/api/generic-search'}/${input}`

  // Allow modification of request by caller prior to send.
  // Primarily used for custom authentication, but can be used to modify any aspect of the request
  if (options?.beforeSend) {
    reqInit = options.beforeSend(reqInput, reqInit)
  }

  const response = await fetch(reqInput, reqInit)

  if (!response.ok) {
    switch (response.status) {
      case 401:
        // TODO: Use proper error codes when error handling is implemented
        throw new Error('SESSION_TIMEOUT')
      case 404:
        throw new Error('NOT_FOUND')
      default:
        const error = (await response.json()) as { code: string }
        if (error?.code !== 'SERVER_ERROR') {
          throw new Error(error.code)
        }
        throw new Error('SERVER_ERROR')
    }
  }

  return response.json()
}

export function getSearchConfig(searchTypeKey: string, options?: RequestOptions) {
  return _makeRequest(`fetch-search-config/${searchTypeKey}`, undefined, options)
}

export function getSearchResult(searchQuery?: SearchQuery, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ searchQuery }))

  return _makeRequest(
    `fetch-search-results`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getLookupResult(lookupQuery: Record<string, string | undefined>, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ lookupQuery }))

  return _makeRequest(
    `fetch-lookup-results`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getLookupDisplayValue(lookupDisplayValueQuery: any, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ lookupDisplayValueQuery }))

  return _makeRequest(
    `fetch-lookup-display-value`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getAddressCoordinates(text: string, key: string, options?: RequestOptions) {
  const queryString = buildQueryString({ text, key })
  return _makeRequest(`esri-address/get-coordinates/?${queryString}`, undefined, options)
}

export function searchAddress(query: string, options?: RequestOptions) {
  const queryString = buildQueryString({ q: query, includePlaces: true })
  return _makeRequest(`esri-address/search-physical/?${queryString}`, undefined, options)
}

export function getDetailedMapResult(
  searchTypeKey: string,
  identifier: string | number | boolean | null,
  options?: RequestOptions,
) {
  const data = new FormData()
  data.append('query', JSON.stringify({ mapDetailQuery: { searchTypeKey, identifier } }))

  return _makeRequest(
    `fetch-detailed-map-result`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}
